import React, { useEffect } from "react";
import { useColorMode } from "../../contexts/ColorModeContext";

function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  title = "Confirm Delete",
  message = "Are you sure you want to delete this customer? This action cannot be undone.",
  confirmText = "Delete",
  cancelText = "Cancel",
  type = "danger",
  ...props
}) {
  // Get current theme mode
  const { mode } = useColorMode();

  // Check whether dark mode is active
  const isDark = mode === "dark";

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // Restore body scroll
      document.body.style.overflow = "auto";
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return "✅";

      case "warning":
        return "⚠️";

      case "info":
        return "ℹ️";

      case "danger":
      default:
        return "⚠️";
    }
  };

  const getConfirmButtonColor = () => {
    switch (type) {
      case "success":
        return "#22c55e";

      case "warning":
        return "#f59e0b";

      case "info":
        return "#3b82f6";

      case "danger":
      default:
        return "#ef4444";
    }
  };

  const showCancelButton = cancelText !== "";

  return (
    <div
      {...props}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,

        // Dark overlay
        backgroundColor: isDark
          ? "rgba(0, 0, 0, 0.70)"
          : "rgba(0, 0, 0, 0.50)",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          // MAIN POPUP BACKGROUND
          backgroundColor: isDark ? "#0F172A" : "#FFFFFF",

          borderRadius: "16px",
          padding: "32px",
          maxWidth: "440px",
          width: "90%",

          // Dark mode border
          border: isDark
            ? "1px solid #334155"
            : "1px solid #E5E7EB",

          // Different shadow for dark/light
          boxShadow: isDark
            ? "0 25px 60px rgba(0, 0, 0, 0.55)"
            : "0 25px 50px -12px rgba(0, 0, 0, 0.25)",

          // Smooth theme transition
          transition:
            "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: "700",

              // TITLE COLOR
              color: isDark ? "#F8FAFC" : "#1F2937",
            }}
          >
            {title}
          </h3>

          {/* CLOSE BUTTON */}
          <button
            onClick={onCancel}
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",

              // Close icon color
              color: isDark ? "#94A3B8" : "#9CA3AF",

              padding: "0",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark
                ? "#273449"
                : "#F3F4F6";

              e.currentTarget.style.color = isDark
                ? "#F8FAFC"
                : "#4B5563";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";

              e.currentTarget.style.color = isDark
                ? "#94A3B8"
                : "#9CA3AF";
            }}
          >
            ×
          </button>
        </div>

        {/* MESSAGE SECTION */}
        <div
          style={{
            marginBottom: "28px",
          }}
        >
          {/* ICON */}
          <div
            style={{
              fontSize: "48px",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            {getIcon()}
          </div>

          {/* MESSAGE */}
          <p
            style={{
              margin: 0,
              fontSize: "15px",

              // MESSAGE COLOR
              color: isDark ? "#CBD5E1" : "#6B7280",

              lineHeight: "1.6",
              textAlign: "center",
            }}
          >
            {message}
          </p>
        </div>

        {/* BUTTONS */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          {/* CANCEL BUTTON */}
          {showCancelButton && (
            <button
              onClick={onCancel}
              style={{
                padding: "12px 24px",

                // Dark/light cancel background
                backgroundColor: isDark ? "#1E293B" : "#F3F4F6",

                // Dark/light text
                color: isDark ? "#CBD5E1" : "#4B5563",

                border: isDark
                  ? "1px solid #334155"
                  : "none",

                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "#273449"
                  : "#E5E7EB";

                e.currentTarget.style.color = isDark
                  ? "#F8FAFC"
                  : "#374151";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "#1E293B"
                  : "#F3F4F6";

                e.currentTarget.style.color = isDark
                  ? "#CBD5E1"
                  : "#4B5563";
              }}
            >
              {cancelText}
            </button>
          )}

          {/* CONFIRM BUTTON */}
          <button
            onClick={onConfirm}
            style={{
              padding: "12px 24px",
              backgroundColor: getConfirmButtonColor(),
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.2s",

              boxShadow: `0 4px 6px -1px ${getConfirmButtonColor()}40`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";

              e.currentTarget.style.boxShadow = `0 6px 8px -1px ${getConfirmButtonColor()}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";

              e.currentTarget.style.boxShadow = `0 4px 6px -1px ${getConfirmButtonColor()}40`;
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;

